/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import { paidAdStatus } from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import PaidAdList from './List';

/**
 * Query to fetch ads from api
 */
export const PAIDADS_QUERY = gql`
  query PaidAdsList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $hasPromotion: Boolean
    $status: [FilterAutoPaidAdStatus]
    $schedule: [FilterAutoPaidAdSchedule]
    $frequency: [RelativeDateTimeInput]
    $frequencyRegex: [ID]
    $startDateGte: DateTime
    $startDateLte: DateTime
    $ad: ID
    $id: ID
    $owner: ID
    $manager: ID
    $pendingReason: [FilterByPendingReason]
    $fromSamePromotion: ID
  ) {
    paidAds(
      first: $first
      after: $after
      last: $last
      before: $before
      paidAdType: AUTO_PAID_AD
      hasPromotion: $hasPromotion
      status: $status
      schedule: $schedule
      frequency: $frequency
      frequencyRegex: $frequencyRegex
      startDateGte: $startDateGte
      startDateLte: $startDateLte
      pendingReason: $pendingReason
      ad: $ad
      id: $id
      owner: $owner
      manager: $manager
      fromSamePromotion: $fromSamePromotion
    ) @connection(key: "paidads") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          startDate
          endDate
          owner {
            id
            fullname
          }
          manager {
            id
            fullname
            userType
          }
          hasPromotion
          duration {
            years
            months
            days
            hours
            minutes
            seconds
          }
          ... on AdminAutoPaidAdType {
            status
            frequency {
              years
              months
              days
              hours
              minutes
              seconds
            }
            schedule
            currency
            userPrice
            commission
            secondaryCurrency
            secondaryUserPrice
            secondaryCommission
            online
          }
          ad {
            id
            title
            subcategory {
              id
              title
              parentCategory {
                id
                title
              }
            }
            mainImage {
              id
              urls {
                thumb
              }
            }
            updatedOnToOrder
            flagCount
            status
            reviewed
            isPublished
            updateCount
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to review ads
 */
export const REVIEW_ADS_MUTATION = gql`
  mutation($ids: [ID]!) {
    reviewAd(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to publish ads
 */
export const PUBLISH_ADS_MUTATION = gql`
  mutation($ids: [ID]!) {
    publishAd(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to extend paidad
 */
export const EXTEND_PAIDAD_MUTATION = gql`
  mutation($ids: [ID]!) {
    extendAutoPaidAd(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to pause paidad
 */
export const PAUSE_PAIDAD_MUTATION = gql`
  mutation($ids: [ID]!) {
    pauseAutoPaidAd(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to resume paidad
 */
export const RESUME_PAIDAD_MUTATION = gql`
  mutation($ids: [ID]!) {
    resumeAutoPaidAd(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to cancel paidad
 */
export const CANCEL_PAIDAD_MUTATION = gql`
  mutation CancelPaidAd($ids: [ID]!) {
    cancelAutoPaidAd(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Default filters in PaidAdsList
 */
export const defaultFilters = {
  hasPromotion: undefined,
  status: undefined,
  schedule: undefined,
  frequency: undefined,
  startDateGte: undefined,
  startDateLte: undefined,
  ad: undefined,
  id: undefined,
  owner: undefined,
  manager: undefined,
  pendingReason: undefined,
  paidAdType: undefined,
  fromSamePromotion: undefined,
};

/**
 * @class
 * @extends Component
 */
class PaidAdListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;

    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected: parseInt(numberOfRowsSelected, 0) || 25,
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      notification: {},
    };
    this.Compose = adopt({
      mutateAdReview: this.adReview,
      mutateAdPublish: this.adPublish,
      mutatePaidAdExtend: this.paidAdExtend,
      mutatePaidAdPause: this.paidAdPause,
      mutatePaidAdResume: this.paidAdResume,
      mutatePaidAdCancel: this.paidAdCancel,
      query: this.listPaidAds,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listPaidAds = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={PAIDADS_QUERY}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation review callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adReview = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={REVIEW_ADS_MUTATION}
        onCompleted={this.mutationCompleted('reviewAd', itemProcessing)}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {handleAdReview => render({ handleAdReview })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation publish callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adPublish = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={PUBLISH_ADS_MUTATION}
        onCompleted={this.mutationCompleted('publishAd', itemProcessing)}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {handleAdPublish => render({ handleAdPublish })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation pause callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  paidAdPause = ({ render }) => {
    const { filters, itemProcessing } = this.state;
    return (
      <Mutation
        mutation={PAUSE_PAIDAD_MUTATION}
        onCompleted={this.mutationCompleted('pauseAutoPaidAd', itemProcessing)}
        update={(cache, { data: { pauseAutoPaidAd } }) => {
          const data = {
            status: paidAdStatus.PAUSED,
            __typename: 'AdminAutoPaidAdType',
          };

          bulkUpdate(
            cache,
            'AdminAutoPaidAdType',
            'pause',
            'status',
            data,
            pauseAutoPaidAd.successIds,
          );

          if (filters.status.indexOf(paidAdStatus.PAUSED) === -1) {
            const variables = { last: pauseAutoPaidAd.successIds.length };
            const { paidAds } = cache.readQuery({
              query: PAIDADS_QUERY,
            });

            const newEdges = paidAds.edges.filter(
              edge => pauseAutoPaidAd.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: PAIDADS_QUERY,
              data: {
                paidAds: {
                  ...paidAds,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {(handlePaidAdPause, { loading }) =>
          render({ handlePaidAdPause, loadingPaidAdPause: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation cancel callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  paidAdCancel = ({ render }) => {
    const { filters, itemProcessing } = this.state;
    return (
      <Mutation
        mutation={CANCEL_PAIDAD_MUTATION}
        onCompleted={this.mutationCompleted('cancelAutoPaidAd', itemProcessing)}
        update={(cache, { data: { cancelAutoPaidAd } }) => {
          const data = {
            status: paidAdStatus.CANCELLED,
            __typename: 'AdminAutoPaidAdType',
          };

          bulkUpdate(
            cache,
            'AdminAutoPaidAdType',
            'cancel',
            'status',
            data,
            cancelAutoPaidAd.successIds,
          );

          if (filters.status.indexOf(paidAdStatus.CANCELLED) === -1) {
            const variables = { last: cancelAutoPaidAd.successIds.length };
            const { paidAds } = cache.readQuery({
              query: PAIDADS_QUERY,
            });

            const newEdges = paidAds.edges.filter(
              edge => cancelAutoPaidAd.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: PAIDADS_QUERY,
              data: {
                paidAds: {
                  ...paidAds,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {(handlePaidAdCancel, { loading }) =>
          render({ handlePaidAdCancel, loadingPaidAdCancel: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation resume callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  paidAdResume = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={RESUME_PAIDAD_MUTATION}
        onCompleted={this.mutationCompleted('resumeAutoPaidAd', itemProcessing)}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {(handlePaidAdResume, { loading }) =>
          render({ handlePaidAdResume, loadingPaidAdResume: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation extend callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  paidAdExtend = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={EXTEND_PAIDAD_MUTATION}
        onCompleted={this.mutationCompleted('extendAutoPaidAd', itemProcessing)}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {(handlePaidAdExtend, { loading }) =>
          render({ handlePaidAdExtend, loadingPaidAdExtend: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const { pageInfo, edges: newEdges } = options.fetchMoreResult.paidAds;
    if (newEdges.length !== 0) {
      const existingIds = new Set(
        previousResult.paidAds.edges.map(e => e.node.id),
      );
      const uniqueNewEdges = newEdges.filter(e => !existingIds.has(e.node.id));
      return {
        paidAds: {
          __typename: previousResult.paidAds.__typename,
          edges: [...previousResult.paidAds.edges, ...uniqueNewEdges],
          pageInfo: {
            ...previousResult.paidAds.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Handle change order.
   *
   * @param {Object} newOrder - Object that contain order mapping.
   */
  handleChangeOrder = newOrder => {
    const history = this.context;
    const {
      appliedOrder: { orderField, orderAsc },
    } = this.state;
    const appliedOrder = {
      orderField: newOrder.val,
      orderAsc: !(orderField === newOrder.val && orderAsc),
      orderColumn: newOrder.column,
    };
    this.setState({ appliedOrder });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({ ...parsed, appliedOrder });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * OnComplete delete mutation.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const { t } = this.props;
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      notification:
        failedIds.length > 0
          ? {
              type: 'failed',
              message: t(`common.failed_bulk_action`, {
                count: failedIds.length,
              }),
            }
          : {},
    });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters, ...filters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ notification: {} });
  };

  /**
   * @description
   * Render AdsList component.
   */
  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      pagination,
      failedIds,
      notification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateAdReview: { handleAdReview },
          mutateAdPublish: { handleAdPublish },
          mutatePaidAdExtend: { handlePaidAdExtend },
          mutatePaidAdPause: { handlePaidAdPause },
          mutatePaidAdResume: { handlePaidAdResume },
          mutatePaidAdCancel: { handlePaidAdCancel },
          query: { loading, data },
        }) => (
          <PaidAdList
            data={data}
            changeOrder={this.handleChangeOrder}
            adReview={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleAdReview({ variables: { ids: items } });
            }}
            adPublish={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleAdPublish({ variables: { ids: items } });
            }}
            paidAdCancel={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handlePaidAdCancel({ variables: { ids: items } });
            }}
            paidAdExtend={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handlePaidAdExtend({ variables: { ids: items } });
            }}
            paidAdPause={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handlePaidAdPause({ variables: { ids: items } });
            }}
            paidAdResume={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handlePaidAdResume({ variables: { ids: items } });
            }}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            refresh={this.handleRefresh}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            restartFilters={this.handleRestartFilters}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            notification={notification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * PaidAdListContainer PropTypes
 * @param t - prop from HOC with string translations
 * @param initialFilters - prop initial Filters to apply
 */

PaidAdListContainer.propTypes = {
  t: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
};

export default withTranslation()(PaidAdListContainer);
